import type { LucideIcon } from "lucide-react";
import {
  ShieldAlert, Plug, Bot, Users, Network, Inbox, UserPlus, ShieldCheck, ClipboardList, Plane,
  FileSpreadsheet, Briefcase, BookMarked, GraduationCap, LogOut, AlertTriangle, HardHat, Search,
  ShoppingBag, ClipboardCheck, Lightbulb, Compass, GitBranch, Map, FileText, Link as LinkIcon,
  Wrench, MessageCircle, KanbanSquare, FolderOpen, FileLock2, Bug, MapPin,
} from "lucide-react";
import { hasPermission, type PermissionKey } from "@/lib/permissions";
import type { Role } from "@/types/db";

export interface NavItem {
  to: string; label: string; Icon: LucideIcon;
  allow?: Role[]; permission?: PermissionKey; gated?: "support";
}
export interface NavGroup { title: string; Icon: LucideIcon; items: NavItem[]; }

// Admin nav, grouped. Each item carries its own permission key; a group only
// shows if the user can see at least one item in it.
//
// Exported because /admin's landing page (HQ-ADMIN-LANDING-001) renders exactly
// what the sidebar would show. Two lists would drift, and a landing page
// offering a link the sidebar does not is a link to somewhere you cannot go.
export const ADMIN_GROUPS: NavGroup[] = [
  // The same six sections as the personal nav (HQ strip-down, 2026-10), then
  // the super-admin tools.
  { title: "Communication", Icon: MessageCircle, items: [
    { to: "/admin/chat", label: "Chat Mgmt", Icon: MessageCircle, permission: "hq.admin.chat" },
    { to: "/admin/suggestions", label: "Suggestions Inbox", Icon: Inbox, permission: "hq.suggestions.view" },
    { to: "/admin/dev-requests", label: "Core Dev Requests", Icon: Wrench, permission: "hq.dev_requests.view" },
    { to: "/admin/bugs", label: "Bug Reports", Icon: Bug, permission: "hq.bugs.view" },
  ] },
  { title: "Organization", Icon: Users, items: [
    { to: "/admin/employees", label: "Employees", Icon: Users, permission: "hq.employees.view" },
    { to: "/admin/org", label: "Reporting Lines", Icon: Network, permission: "hq.employees.edit" },
    { to: "/admin/jobs", label: "Functions & Hiring", Icon: Briefcase, permission: "hq.admin.jobs" },
    { to: "/admin/provisioning", label: "New-Hire Provisioning", Icon: UserPlus, permission: "hq.employees.invite" },
    { to: "/admin/offboarding", label: "Offboarding", Icon: LogOut, permission: "hq.admin.offboarding" },
    { to: "/admin/access", label: "Access Review", Icon: ShieldCheck, permission: "hq.access_review.view" },
    { to: "/admin/board-access", label: "Board Access", Icon: KanbanSquare, permission: "hq.boards.manage_access" },
    { to: "/admin/objectives", label: "Mission & Objectives", Icon: Compass, permission: "hq.mission.edit" },
    { to: "/admin/decisions", label: "Decision Log", Icon: GitBranch, permission: "hq.admin.decisions" },
    { to: "/admin/reviews", label: "Reviews", Icon: ClipboardList, permission: "hq.reviews.write" },
    { to: "/admin/discipline", label: "Discipline", Icon: AlertTriangle, permission: "hq.admin.discipline" },
    { to: "/admin/safety", label: "Safety", Icon: HardHat, permission: "hq.admin.safety" },
    { to: "/admin/background-checks", label: "Background Checks", Icon: Search, permission: "hq.admin.background_checks" },
  ] },
  { title: "Time Clock", Icon: FileSpreadsheet, items: [
    { to: "/admin/time-payroll", label: "Time Tracking", Icon: FileSpreadsheet, permission: "hq.admin.time_payroll" },
    { to: "/admin/pto", label: "Time Off Requests", Icon: Plane, permission: "hq.admin.pto" },
  ] },
  { title: "SOP Library", Icon: FileText, items: [
    { to: "/admin/sops", label: "Manage SOPs", Icon: FileText, permission: "hq.sops.manage" },
    { to: "/admin/training", label: "Training Modules", Icon: BookMarked, permission: "hq.admin.training" },
    { to: "/admin/training-status", label: "Training Status", Icon: GraduationCap, permission: "hq.training.assign" },
    { to: "/admin/checklists", label: "Checklists", Icon: ClipboardCheck, permission: "hq.admin.checklists" },
  ] },
  { title: "Document Library", Icon: FolderOpen, items: [
    { to: "/admin/documents", label: "Manage Documents", Icon: LinkIcon, permission: "hq.admin.resources" },
    { to: "/admin/drive", label: "Google Drive", Icon: FolderOpen, permission: "hq.admin.drive" },
  ] },
  { title: "Team Board", Icon: KanbanSquare, items: [
    { to: "/admin/roadmap", label: "Software Roadmap", Icon: Map, permission: "hq.admin.roadmap" },
  ] },
  { title: "Admin Tools", Icon: ShieldAlert, items: [
    { to: "/admin/infrastructure", label: "Infrastructure", Icon: Plug, permission: "hq.infrastructure.view" },
    { to: "/admin/integrations", label: "Integrations", Icon: Plug, permission: "hq.infrastructure.view" },
    // The one place an outside client reads company data, so it gets its own
    // entry rather than living inside Integrations as a card somebody scrolls past.
    { to: "/admin/mcp", label: "MCP Server", Icon: Bot, permission: "hq.mcp.manage" },
    { to: "/admin/audit", label: "Audit Log", Icon: FileLock2, permission: "hq.admin.audit" },
    // The public "Stores near you" map's editor; it outlived the Sales CRM.
    { to: "/admin/store-locator", label: "Store Locator", Icon: MapPin, permission: "hq.admin.store_locator" },
    { to: "/admin/product-dev", label: "Product Development", Icon: Lightbulb, permission: "hq.admin.product_dev" },
    { to: "/admin/orders", label: "Order Queue", Icon: ShoppingBag, permission: "hq.admin.orders" },
    { to: "/d2c/customer-journey", label: "Customer Journey", Icon: Network, permission: "d2c.journey.view" },
  ] },
];


/**
 * Can this employee see this nav item?
 *
 * HQ-ADMIN-LANDING-001. This lives beside the list rather than inside Layout so
 * /admin's landing page asks the same question, rather than a second copy of it
 * that drifts. Admin items carry a permission key — admins and super_admins get
 * them through role defaults; a VA sees only what extra_permissions grants.
 */
export function canSeeNavItem(
  i: NavItem,
  employee: { role?: Role | null; extra_permissions?: string[] | null; denied_permissions?: string[] | null; can_access_support?: boolean | null } | null | undefined,
): boolean {
  const role = employee?.role ?? undefined;
  if (i.allow && role && !i.allow.includes(role)) return false;
  if (i.gated === "support") {
    if (employee?.can_access_support) return true;
    return role === "admin" || role === "super_admin";
  }
  if (i.permission) return hasPermission(role, employee?.extra_permissions ?? null, i.permission, employee?.denied_permissions ?? null);
  return true;
}
